import { useEffect } from "react";

/**
 * Lightweight SEO hook: sets document title, meta description, canonical,
 * OG/Twitter tags, and injects JSON-LD structured data.
 * Cleans up on unmount so SPA route changes don't leak stale tags.
 */
export function useSEO({ title, description, canonical, image, schema }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (name, content, isProperty = false) => {
      if (!content) return null;
      const attr = isProperty ? "property" : "name";
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      const created = !tag;
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
      return { tag, created };
    };

    const setLink = (rel, href) => {
      if (!href) return null;
      let tag = document.querySelector(`link[rel="${rel}"]`);
      const created = !tag;
      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", rel);
        document.head.appendChild(tag);
      }
      tag.setAttribute("href", href);
      return { tag, created };
    };

    const created = [];
    const push = (r) => r && created.push(r);

    push(setMeta("description", description));
    push(setMeta("og:title", title, true));
    push(setMeta("og:description", description, true));
    push(setMeta("og:type", "website", true));
    push(setMeta("og:image", image, true));
    push(setMeta("twitter:card", "summary_large_image"));
    push(setMeta("twitter:title", title));
    push(setMeta("twitter:description", description));
    push(setMeta("twitter:image", image));
    push(setLink("canonical", canonical || window.location.href.split("?")[0]));

    let jsonLdEl = null;
    if (schema) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.text = JSON.stringify(schema);
      jsonLdEl.setAttribute("data-seo", "true");
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = prevTitle;
      created.forEach((r) => { if (r.created && r.tag) r.tag.remove(); });
      if (jsonLdEl) jsonLdEl.remove();
    };
  }, [title, description, canonical, image, JSON.stringify(schema)]);
}
